/**
 * Daily-Quest catalog — short, completeable-in-one-session goals that reset
 * every day at 04:00 UTC. The seed is `Date.toISOString().slice(0,10)` so
 * the player gets a deterministic but rotating selection — same day across
 * devices yields the same quest, no server roundtrip needed.
 *
 * Quest-Pool size > daily-pick-count, so players see novelty over time.
 * Player engagement loop: open game → see today's quest → play one focused
 * run → claim 100 CD + 1 Ascension-Fragment.
 */

// Re-use the AchievementTrigger union so quest objectives and achievement
// triggers share the same event-bus — one bumpEvent call updates both.
import type { AchievementTrigger } from './achievements';

export type QuestObjective = AchievementTrigger;

export interface DailyQuestDef {
  id: string;
  nameKey: string;
  descriptionKey: string;
  iconKey: string;
  /** Same event-trigger union as Achievements — one event-bus to rule them all. */
  objective: QuestObjective;
  /** How many event-fires to complete the quest. */
  targetProgress: number;
  reward: { corruptedData: number; ascensionFragments: number };
}

export const DAILY_QUESTS: Record<string, DailyQuestDef> = {
  kill_50: {
    id: 'kill_50', nameKey: 'quest.kill_50', descriptionKey: 'quest.kill_50_desc',
    iconKey: 'sword', objective: 'enemy_killed', targetProgress: 50,
    reward: { corruptedData: 100, ascensionFragments: 1 },
  },
  kill_200: {
    id: 'kill_200', nameKey: 'quest.kill_200', descriptionKey: 'quest.kill_200_desc',
    iconKey: 'sword', objective: 'enemy_killed', targetProgress: 200,
    reward: { corruptedData: 300, ascensionFragments: 1 },
  },
  clear_vault_1: {
    id: 'clear_vault_1', nameKey: 'quest.clear_vault_1', descriptionKey: 'quest.clear_vault_1_desc',
    iconKey: 'swords', objective: 'vault_cleared', targetProgress: 1,
    reward: { corruptedData: 150, ascensionFragments: 1 },
  },
  clear_vault_3: {
    id: 'clear_vault_3', nameKey: 'quest.clear_vault_3', descriptionKey: 'quest.clear_vault_3_desc',
    iconKey: 'swords', objective: 'vault_cleared', targetProgress: 3,
    reward: { corruptedData: 400, ascensionFragments: 2 },
  },
  boss_1: {
    id: 'boss_1', nameKey: 'quest.boss_1', descriptionKey: 'quest.boss_1_desc',
    iconKey: 'skull', objective: 'boss_killed', targetProgress: 1,
    reward: { corruptedData: 250, ascensionFragments: 1 },
  },
  endless_wave_5: {
    id: 'endless_wave_5', nameKey: 'quest.endless_wave_5', descriptionKey: 'quest.endless_wave_5_desc',
    iconKey: 'zap', objective: 'endless_wave_reached', targetProgress: 5,
    reward: { corruptedData: 200, ascensionFragments: 1 },
  },
  endless_wave_15: {
    id: 'endless_wave_15', nameKey: 'quest.endless_wave_15', descriptionKey: 'quest.endless_wave_15_desc',
    iconKey: 'zap', objective: 'endless_wave_reached', targetProgress: 15,
    reward: { corruptedData: 500, ascensionFragments: 2 },
  },
  upgrade_1: {
    id: 'upgrade_1', nameKey: 'quest.upgrade_1', descriptionKey: 'quest.upgrade_1_desc',
    iconKey: 'hexagon', objective: 'item_upgraded_to_max', targetProgress: 1,
    reward: { corruptedData: 150, ascensionFragments: 1 },
  },
  ascend_1: {
    id: 'ascend_1', nameKey: 'quest.ascend_1', descriptionKey: 'quest.ascend_1_desc',
    iconKey: 'sparkles', objective: 'item_ascended', targetProgress: 1,
    reward: { corruptedData: 300, ascensionFragments: 2 },
  },
  legendary_1: {
    id: 'legendary_1', nameKey: 'quest.legendary_1', descriptionKey: 'quest.legendary_1_desc',
    iconKey: 'sparkles', objective: 'legendary_dropped', targetProgress: 1,
    reward: { corruptedData: 350, ascensionFragments: 2 },
  },
};

/** Daily reset cutoff: 04:00 UTC. Returns epoch ms of the NEXT reset. */
export function nextResetAt(now: number = Date.now()): number {
  const d = new Date(now);
  const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 4, 0, 0, 0));
  if (next.getTime() <= now) next.setUTCDate(next.getUTCDate() + 1);
  return next.getTime();
}

/** Deterministic daily quest pick: hashes the YYYY-MM-DD seed against the
 *  quest-pool, plus the playerId so two players see different quests on the
 *  same day. Returns the QuestDef to use today. */
export function pickDailyQuest(playerId: string, now: number = Date.now()): DailyQuestDef {
  const dateSeed = new Date(now).toISOString().slice(0, 10);
  const seed = `${dateSeed}::${playerId}`;
  // djb2 hash — small, fast, no deps
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) | 0;
  const ids = Object.keys(DAILY_QUESTS);
  const idx = Math.abs(h) % ids.length;
  return DAILY_QUESTS[ids[idx]];
}

export function getDailyQuest(id: string): DailyQuestDef | undefined {
  return DAILY_QUESTS[id];
}
